import React, { createContext, useEffect, useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import Navbar from "../../Components/Navbar";
import { AppContextProps } from "../../Context/AppContext";
import { Endpoints } from "../../Lib/Endpoints";
import { NonPaginatedResponse } from "../../Lib/Responses";
import {
  DurationDiscountProps,
  googleOneStoragePricingObjectProps,
  iCloudAppleMusicPricingObjectProps,
  PossiblePeriodsCostProps,
  PricingObjectProps,
  UserProfile,
} from "../../Lib/Types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import MySubscriptions from "../MySubscriptions";
import RegisterYoutube from "../RegisterYoutube";
import Services from "../Services";
import MyTransactions from "../Transactions";
import SingleSubscriptionPage from "../SingleSubscription";
import SupportPage from "../Support";
import PromoterScreen from "../Promoter";

import "./styles.scss";
import {
  DefaultErrorNotification,
  DefaultMessageNotification,
  DefaultSuccessNotification,
} from "../../Lib/Methods";
import CreateSupport from "../CreateSupport";
import SingleSupport from "../SingleSupport";
import { notifications } from "@mantine/notifications";
import { Button, Notification, Text } from "@mantine/core";
import { Colors } from "../../Lib/Data";
import { IconCheck, IconExclamationCircle } from "@tabler/icons-react";
import MegaLoader from "../../Components/MegaLoader";
import ProfilePage from "../Profile";
import LoginFromAdmin from "../LoginFromAdmin";
import { useWindowScroll } from "@mantine/hooks";
import WelcomeOfferModal from "../../Components/Modals/WelcomeOfferModal";
import SuccessfulPayment from "../Payment/Success";
export const AppContext = createContext<AppContextProps>(undefined);
export default function Wrapper() {
  const navigate = useNavigate();
  const [showUnverified, setShowUnverified] = useState<boolean>(false);

  const {
    data: profile,
    isLoading: isLoadingProfile,
    reloadData: reloadProfile,
  } = usePerformRequest<NonPaginatedResponse<UserProfile>>({
    method: "GET",
    route: Endpoints.GetUserProfile(),
  });

  const { data: pricing, isLoading: isLoadingPricing } = usePerformRequest<
    NonPaginatedResponse<{
      pricing: PricingObjectProps[];
      durationDiscounts: DurationDiscountProps[];
      possiblePeriodsCost: PossiblePeriodsCostProps[];
      iCloudAppleMusicPricing: iCloudAppleMusicPricingObjectProps[];
      googleOneStoragePricing: googleOneStoragePricingObjectProps[];
    }>
  >({
    route: Endpoints.GetPricingArray(),
    method: "GET",
  });
  const { data: unregistered, reloadData: reloadUnregistered } =
    usePerformRequest<NonPaginatedResponse<string[]>>({
      route: Endpoints.GetUnregisteredTemps(),
      method: "GET",
    });

  useEffect(() => {
    if (unregistered && unregistered.data) {
      const u = unregistered.data[0];
      if (u) {
        DefaultMessageNotification(
          "You have an unregistered YouTube subscription",
        );
        navigate(`/d/youtube/${u}`);
      }
    }
  }, [unregistered]);
  useEffect(() => {
    if (profile && profile.data) {
      setShowUnverified(!profile.data.isVerified);
    }
  }, [profile]);
  useEffect(() => {
    const existingTheme = localStorage.getItem("theme");
    if (existingTheme) {
      setTheme(existingTheme as "light" | "dark");
    } else {
      const prefersDark = window.matchMedia("(prefers-color-scheme: dark)");
      // if (prefersDark.matches) {
      //   setTheme("dark");
      // } else {
      //   setTheme("light");
      // }
    }
  }, []);

  const [isSendingLink, setSendingLink] = useState<boolean>(false);
  const [isLinkSent, setLinkSent] = useState<boolean>(false);

  const ResendEmail = async () => {
    setSendingLink(true);
    const r = await PerformRequest<NonPaginatedResponse<any>>({
      route: Endpoints.GetResendVerificationLink(),
      method: "GET",
      callback: () => {
        setSendingLink(false);
      },
    });
    setSendingLink(false);
    if (r && r.status === 200) {
      setLinkSent(true);
    }
  };

  const location = useLocation();

  const [scroll, scrollTo] = useWindowScroll();
  useEffect(() => {
    scrollTo({ x: 0, y: 0 });
  }, [location]);

  const logout = () => {
    localStorage.removeItem("t");
    localStorage.removeItem("a");
    navigate("/login");
  };
  const [theme, setTheme] = useState<"light" | "dark">("light");

  const [isWelcomeModalOpened, setWelcomeModalOpened] = useState(false);

  useEffect(() => {
    const hasSeen = localStorage.getItem("hasSeenWelcomeOffer");
    if (!hasSeen) {
      // Small delay to let the page load a bit before showing pop-up
      const timer = setTimeout(() => {
        setWelcomeModalOpened(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const closeWelcomeModal = () => {
    setWelcomeModalOpened(false);
    localStorage.setItem("hasSeenWelcomeOffer", "true");
  };

  return (
    <AppContext.Provider
      value={{
        logout,
        theme,
        setTheme,
        isLoadingPricing,
        pricing: pricing?.data,

        profile: profile?.data,
        reloadProfile,
        isLoadingProfile,
        reloadUnregistered,
      }}
    >
      <div className={`wrapper-container wrapper-${theme}`}>
        <Navbar />

        {profile && profile.data && profile.data.id && (
          <>
            {showUnverified && (
              <div className="unverified-container">
                <Notification
                  withCloseButton={true}
                  onClose={() => setShowUnverified(false)}
                  color={isLinkSent ? Colors.GreenPrimary : Colors.BluePrimary}
                  title={isLinkSent ? "Check your Inbox" : "Confirm your email"}
                >
                  <>
                    <div className="flex-col">
                      <Text fz="sm" fw={500}>
                        {isLinkSent
                          ? "Email has been resent"
                          : "Your email has not been verified"}
                      </Text>
                      <Text fz="sm">
                        Please check your inbox for a confirmation email from us
                      </Text>
                      <Button
                        mt={10}
                        w={140}
                        disabled={isLinkSent}
                        onClick={ResendEmail}
                        loading={isSendingLink}
                        color={
                          isLinkSent ? Colors.GreenPrimary : Colors.BluePrimary
                        }
                      >
                        {isLinkSent ? "Sent!" : "Resend Email"}
                      </Button>
                    </div>
                  </>
                </Notification>
              </div>
            )}
          </>
        )}
        <Outlet />

        <WelcomeOfferModal
          opened={isWelcomeModalOpened}
          onClose={closeWelcomeModal}
        />
      </div>
    </AppContext.Provider>
  );
}
